// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Badge, Box, Divider, Group, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { createReference, normalizeErrorString } from '@medplum/core';
import type { Practitioner, Reference, Task } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import type { TriageSortState } from '@polarissleep/shared';
import { PATIENT_TRIAGE_STATUS } from '@polarissleep/shared';
import { IconAlertTriangle, IconCircleCheck, IconCircleCheckFilled, IconCircleOff } from '@tabler/icons-react';
import type { DataTableSortStatus } from 'mantine-datatable';
import { DataTable } from 'mantine-datatable';
import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { TableAnchor } from '../../components/layout/TableAnchor';
import { useRole } from '../../hooks/useRole';
import type { TriageCompositionData } from '../../hooks/useTriageQueue';
import { useTriageQueue } from '../../hooks/useTriageQueue';
import { hasPermission } from '../../utils/permissions';
import { withTenantScope } from '../../utils/tenant';
import { CreateTriageTaskModal, type TaskDetails } from './CreateTriageTaskModal';
import { IntakeEvaluationModal } from './IntakeEvaluationModal';
import { TriagePatientDrawer } from './TriagePatientDrawer';
import {
  getEligibilitySignal,
  getInsuranceEligibility,
  shouldShowClinicalExclusionAlert,
  shouldShowEpworthNotMedicallyNecessaryAlert,
  shouldShowInsuranceIneligibleAlert,
  shouldShowStopBangNotMedicallyNecessaryAlert,
  TRIAGE_ALERT_ARIA_LABELS,
} from './triageAlertRules';
import { sortStatusToTriageSortState, triageSortStateToSortStatus } from './useTriageQueueUrlState';

interface IntakeTableProps {
  page: number;
  pageSize: number;
  search?: string;
  sortState: TriageSortState | null;
  onPageChange: (page: number) => void;
  onSortChange: (next: TriageSortState | null) => void;
  hideTestPatients: boolean;
  onTotalChange?: (total: number) => void;
  activeStage?: string;
}

const INTAKE_TASK_IDENTIFIER_SYSTEM = 'https://polarissleep.com/fhir/task/intake-review';
const OPEN_TASK_STATUS_SEARCH = 'draft,requested,ready,received,accepted,in-progress';

function formatDate(dateString: string | undefined): string {
  if (!dateString) {
    return '-';
  }
  const date = new Date(dateString);
  if (!Number.isFinite(date.getTime())) {
    return '-';
  }
  const now = new Date();
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString(
    undefined,
    sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' }
  );
}

function splitPatientName(name: string): { primary: string; secondary?: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) {
    return { primary: name };
  }
  return {
    primary: parts.slice(0, -1).join(' '),
    secondary: parts[parts.length - 1],
  };
}

function formatPatientSubtitle(comp: TriageCompositionData): string | undefined {
  let sexLabel = '';
  if (comp.sexAtBirth === 'M') {
    sexLabel = 'Male';
  } else if (comp.sexAtBirth === 'F') {
    sexLabel = 'Female';
  } else if (comp.sexAtBirth === 'U') {
    sexLabel = 'Unknown';
  }
  const ageLabel = comp.age !== undefined ? `${comp.age}` : '';

  if (!ageLabel && !sexLabel) {
    return undefined;
  }
  return [ageLabel, sexLabel].filter(Boolean).join(' • ');
}

function renderMetric(value: string, label: string, tone: 'default' | 'alert' = 'default'): JSX.Element {
  return (
    <Stack gap={0} style={{ minWidth: 0 }}>
      <Text size="lg" fw={700} c={tone === 'alert' ? '#C66A12' : 'dark'} style={{ lineHeight: 1.1 }}>
        {value}
      </Text>
      <Text size="10px" fw={700} c="dimmed" tt="uppercase" style={{ lineHeight: 1.15, letterSpacing: '0.04em' }}>
        {label}
      </Text>
    </Stack>
  );
}

function renderCountPill(
  singular: string,
  values: { display: string }[],
  tone: 'gray' | 'orange' | 'green'
): JSX.Element {
  let palette = { bg: '#F8F9FA', color: '#868E96', border: '#DEE2E6' };
  if (tone === 'orange') {
    palette = { bg: '#FFF4E6', color: '#C66A12', border: '#FFE0B2' };
  } else if (tone === 'green') {
    palette = { bg: '#EBFBEE', color: '#2F9E44', border: '#D3F9D8' };
  }

  let text = 'None';
  if (values.length > 0) {
    const suffix = values.length === 1 ? '' : 's';
    text = `${values.length} ${singular}${suffix}`;
  }

  const badge = (
    <Badge
      variant="light"
      radius="xl"
      styles={{
        root: {
          textTransform: 'none',
          backgroundColor: palette.bg,
          color: palette.color,
          border: `1px solid ${palette.border}`,
          cursor: values.length > 0 ? 'default' : undefined,
        },
      }}
    >
      {text}
    </Badge>
  );

  if (values.length === 0) {
    return badge;
  }

  return (
    <Tooltip
      multiline
      withArrow
      label={
        <Stack gap={6}>
          {values.map((value) => (
            <Text key={value.display} size="sm">
              {value.display}
            </Text>
          ))}
        </Stack>
      }
    >
      {badge}
    </Tooltip>
  );
}

function renderEligibilitySignal(comp: TriageCompositionData): JSX.Element {
  const label = getEligibilitySignal(comp);
  const flagged = label === 'Flagged for Review';
  const incomplete = label === 'Incomplete Assessment';
  let palette = { bg: '#EBFBEE', color: '#2F9E44', border: '#D3F9D8' };
  if (incomplete) {
    palette = { bg: '#F1F3F5', color: '#868E96', border: '#DEE2E6' };
  } else if (flagged) {
    palette = { bg: '#FFF4E6', color: '#C66A12', border: '#FFE0B2' };
  }
  let icon: JSX.Element = <IconCircleCheck size={14} />;
  if (incomplete) {
    icon = <IconCircleOff size={14} />;
  } else if (flagged) {
    icon = <IconAlertTriangle size={14} aria-label={TRIAGE_ALERT_ARIA_LABELS.eligibilitySignal} />;
  }
  let detail = 'HSAT criteria met';
  if (incomplete) {
    detail = 'HSAT assessment incomplete';
  } else if (flagged) {
    detail = 'HSAT criteria require review';
  }

  return (
    <Tooltip label={detail} withArrow>
      <Badge
        leftSection={icon}
        variant="light"
        radius="xl"
        styles={{
          root: {
            textTransform: 'none',
            backgroundColor: palette.bg,
            color: palette.color,
            border: `1px solid ${palette.border}`,
          },
        }}
      >
        {label}
      </Badge>
    </Tooltip>
  );
}

function renderReviewStatus(comp: TriageCompositionData): JSX.Element {
  const flagged = comp.ccEvaluationStatus === 'failure' || shouldShowClinicalExclusionAlert(comp);
  const reviewed = comp.triageStatus === PATIENT_TRIAGE_STATUS.hsatApprovedCm || comp.ccEvaluationStatus === 'success';
  let palette = { bg: '#F1F3F5', color: '#868E96', text: 'Not reviewed' };
  if (flagged) {
    palette = { bg: '#FFF4E6', color: '#C66A12', text: 'Flagged for clinical' };
  } else if (reviewed) {
    palette = {
      bg: '#EBFBEE',
      color: '#2F9E44',
      text: comp.ccEvaluatedBy ? `Reviewed by ${comp.ccEvaluatedBy}` : 'Reviewed',
    };
  }

  let statusIcon: JSX.Element = <IconCircleCheck size={12} />;
  if (reviewed) {
    statusIcon = <IconCircleCheckFilled size={14} />;
  } else if (flagged) {
    statusIcon = <IconAlertTriangle size={14} />;
  }

  return (
    <Group gap={10} wrap="nowrap">
      <ThemeIcon
        size="sm"
        radius="xl"
        variant="light"
        styles={{
          root: {
            backgroundColor: palette.bg,
            color: palette.color,
            flexShrink: 0,
          },
        }}
      >
        {statusIcon}
      </ThemeIcon>
      <Text size="sm" fw={600} c={reviewed || flagged ? palette.color : undefined} style={{ lineHeight: 1.25 }}>
        {palette.text}
      </Text>
    </Group>
  );
}

function buildIntakeTaskIdentifier(comp: TriageCompositionData): string {
  return `${comp.patientId ?? 'unknown'}|${comp.compositionId}|intake-review`;
}

function isMatchingIntakeTask(task: Task, comp: TriageCompositionData): boolean {
  return task.code?.text === 'Intake review' && task.for?.reference === `Patient/${comp.patientId}`;
}

export function IntakeTable({
  page,
  pageSize,
  search,
  sortState,
  onPageChange,
  onSortChange,
  hideTestPatients,
  onTotalChange,
  activeStage = 'Intake',
}: IntakeTableProps): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { role, isAdmin } = useRole();
  const canEvaluate = hasPermission({ role, permission: 'evaluate-triage', isAdmin });
  const canPlaceHSATOrder = hasPermission({ role, permission: 'place-hsat-order', isAdmin });
  const { compositions, loading, total } = useTriageQueue({
    page,
    pageSize,
    activeStage,
    search,
    sort: sortState ?? undefined,
    hideTestPatients,
  });

  const [evaluateTarget, setEvaluateTarget] = useState<TriageCompositionData | null>(null);
  const [taskTarget, setTaskTarget] = useState<TriageCompositionData | null>(null);
  const [creatingTaskKey, setCreatingTaskKey] = useState<string | null>(null);
  const [patientDrawerTarget, setPatientDrawerTarget] = useState<TriageCompositionData | null>(null);

  useEffect(() => {
    onTotalChange?.(total);
  }, [onTotalChange, total]);

  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  const handleCreateTask = async (
    comp: TriageCompositionData,
    owner: Reference<Practitioner>,
    details: TaskDetails
  ): Promise<void> => {
    if (!comp.patientId) {
      return;
    }
    const taskKey = buildIntakeTaskIdentifier(comp);
    setCreatingTaskKey(taskKey);
    try {
      const [existingIdentifiedTasks, existingPatientTasks] = await Promise.all([
        medplum.searchResources('Task', {
          identifier: `${INTAKE_TASK_IDENTIFIER_SYSTEM}|${taskKey}`,
          status: OPEN_TASK_STATUS_SEARCH,
          _count: '1',
        }),
        medplum.searchResources('Task', {
          patient: `Patient/${comp.patientId}`,
          status: OPEN_TASK_STATUS_SEARCH,
          _count: '50',
        }),
      ]);

      if (existingIdentifiedTasks.length > 0 || existingPatientTasks.some((task) => isMatchingIntakeTask(task, comp))) {
        notifications.show({
          color: 'blue',
          icon: <IconCircleCheck size={16} />,
          message: 'An open intake-review task already exists for this patient.',
        });
        setTaskTarget(null);
        return;
      }

      const task: Task = {
        resourceType: 'Task',
        identifier: [{ system: INTAKE_TASK_IDENTIFIER_SYSTEM, value: taskKey }],
        status: 'requested',
        intent: 'order',
        priority: details.priority,
        restriction: details.dueDate ? { period: { end: `${details.dueDate}T23:59:59.000Z` } } : undefined,
        authoredOn: new Date().toISOString(),
        requester: profile ? createReference(profile) : undefined,
        owner,
        for: { reference: `Patient/${comp.patientId}`, display: comp.patientName },
        code: { text: 'Intake review' },
        description: details.description,
      };
      await medplum.createResource(withTenantScope(task));
      notifications.show({
        color: 'green',
        icon: <IconCircleCheck size={16} />,
        message: 'Intake-review task created',
      });
      setTaskTarget(null);
    } catch (error) {
      notifications.show({
        color: 'red',
        icon: <IconCircleOff size={16} />,
        message: normalizeErrorString(error),
      });
    } finally {
      setCreatingTaskKey(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessor: 'patientName',
        title: 'Patient',
        sortable: true,
        width: 180,
        render: (comp: TriageCompositionData) => {
          const name = splitPatientName(comp.patientName);
          return (
            <Stack gap={1}>
              <Group gap={6} wrap="nowrap">
                <TableAnchor
                  size="sm"
                  fw={700}
                  onClick={() => setPatientDrawerTarget(comp)}
                  style={{ cursor: 'pointer', lineHeight: 1.15 }}
                >
                  <Stack gap={0}>
                    <Text inherit span>
                      {name.primary}
                    </Text>
                    {name.secondary && (
                      <Text inherit span>
                        {name.secondary}
                      </Text>
                    )}
                  </Stack>
                </TableAnchor>
                {comp.isTestPatient && (
                  <Badge color="orange" variant="light" size="xs" radius="xl">
                    Test
                  </Badge>
                )}
              </Group>
              {formatPatientSubtitle(comp) && (
                <Text size="xs" c="dimmed">
                  {formatPatientSubtitle(comp)}
                </Text>
              )}
            </Stack>
          );
        },
      },
      {
        accessor: 'intakeDate',
        title: 'Intake appt',
        sortable: true,
        width: 140,
        titleStyle: { whiteSpace: 'nowrap' },
        render: (comp: TriageCompositionData) => <Text size="sm">{formatDate(comp.date)}</Text>,
      },
      {
        accessor: 'insurance',
        title: 'Insurance',
        sortable: true,
        width: 150,
        render: (comp: TriageCompositionData) => (
          <Text size="sm">{comp.isSelfPay ? 'Self Pay' : comp.insuranceOrganization || 'Unknown'}</Text>
        ),
      },
      {
        accessor: 'stop-bang',
        title: 'Screener',
        sortable: true,
        width: 220,
        render: (comp: TriageCompositionData) => (
          <Stack gap={6}>
            <Group gap="sm" wrap="nowrap" align="stretch">
              {renderMetric(
                comp.stopBangScore !== undefined ? String(comp.stopBangScore) : '-',
                'STOP-BANG',
                shouldShowStopBangNotMedicallyNecessaryAlert(comp) ? 'alert' : 'default'
              )}
              <Divider orientation="vertical" />
              {renderMetric(
                comp.epworthScore !== undefined ? String(comp.epworthScore) : '-',
                'ESS',
                shouldShowEpworthNotMedicallyNecessaryAlert(comp) ? 'alert' : 'default'
              )}
              <Divider orientation="vertical" />
              {renderMetric(comp.bmi !== undefined ? String(comp.bmi) : '-', 'BMI')}
            </Group>
            <Text size="xs" c="dimmed">
              Date screener was initiated: {formatDate(comp.date)}
            </Text>
          </Stack>
        ),
      },
      {
        accessor: 'comorbidities',
        title: 'Comorbidities',
        sortable: true,
        width: 140,
        render: (comp: TriageCompositionData) => renderCountPill('condition', comp.comorbidities, 'gray'),
      },
      {
        accessor: 'exclusions',
        title: 'Exclusions',
        sortable: true,
        width: 140,
        render: (comp: TriageCompositionData) =>
          renderCountPill('exclusion', comp.exclusions, comp.exclusions.length > 0 ? 'orange' : 'gray'),
      },
      {
        accessor: 'recommendation',
        title: 'HSAT Eligibility',
        sortable: true,
        width: 210,
        render: (comp: TriageCompositionData) => renderEligibilitySignal(comp),
      },
      {
        accessor: 'status',
        title: 'Review status',
        sortable: false,
        width: 180,
        render: (comp: TriageCompositionData) => renderReviewStatus(comp),
      },
      {
        accessor: 'actions',
        title: 'Actions',
        sortable: false,
        width: 132,
        render: (comp: TriageCompositionData) => {
          const canShowEvaluate =
            (canEvaluate && comp.triageStatus === PATIENT_TRIAGE_STATUS.intake) ||
            (canPlaceHSATOrder && comp.triageStatus === PATIENT_TRIAGE_STATUS.hsatApprovedCm);
          const creatingTask = creatingTaskKey === buildIntakeTaskIdentifier(comp);
          return (
            <Stack gap={4} align="flex-start">
              <TableAnchor
                size="sm"
                fw={700}
                c="blue"
                onClick={() => navigate(`/triage/composition/${comp.compositionId}`)}
                style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                View Screener
              </TableAnchor>
              {comp.patientId && (
                <TableAnchor
                  size="sm"
                  component="button"
                  type="button"
                  disabled={creatingTask}
                  onClick={() => setTaskTarget(comp)}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {creatingTask ? 'Creating…' : 'Create Task'}
                </TableAnchor>
              )}
              {canShowEvaluate && (
                <TableAnchor
                  size="sm"
                  fw={700}
                  c="#640040"
                  onClick={() => {
                    if (comp.triageStatus === PATIENT_TRIAGE_STATUS.hsatApprovedCm) {
                      navigate(`/OrderHSAT/${comp.patientId}`);
                    } else {
                      setEvaluateTarget(comp);
                    }
                  }}
                  style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  {comp.triageStatus === PATIENT_TRIAGE_STATUS.hsatApprovedCm ? 'Order HSAT' : 'Evaluate'}
                </TableAnchor>
              )}
            </Stack>
          );
        },
      },
    ],
    [canEvaluate, canPlaceHSATOrder, creatingTaskKey, navigate]
  );

  const sortStatus: DataTableSortStatus<TriageCompositionData> = (() => {
    const status = triageSortStateToSortStatus(sortState);
    if (!status) {
      return { columnAccessor: '', direction: 'desc' as const };
    }
    const columnAccessor = status.columnAccessor === 'name' ? 'patientName' : status.columnAccessor;
    return { columnAccessor, direction: status.direction };
  })();

  return (
    <>
      <IntakeEvaluationModal
        opened={evaluateTarget !== null}
        onClose={() => setEvaluateTarget(null)}
        patientId={evaluateTarget?.patientId}
        patientName={evaluateTarget?.patientName ?? ''}
        isAlreadyApproved={false}
      />

      <TriagePatientDrawer
        composition={patientDrawerTarget}
        opened={patientDrawerTarget !== null}
        onClose={() => setPatientDrawerTarget(null)}
      />

      <CreateTriageTaskModal
        opened={taskTarget !== null}
        title="Intake review"
        description="Review intake screener and complete triage evaluation."
        patientName={taskTarget?.patientName}
        defaultAssignee={profile as Practitioner | undefined}
        submitting={taskTarget ? creatingTaskKey === buildIntakeTaskIdentifier(taskTarget) : false}
        onClose={() => setTaskTarget(null)}
        onCreate={(owner, details) => {
          if (taskTarget) {
            handleCreateTask(taskTarget, owner, details).catch((error) => {
              notifications.show({
                color: 'red',
                icon: <IconCircleOff size={16} />,
                message: normalizeErrorString(error),
              });
            });
          }
        }}
      />

      {total > 0 && (
        <Text size="sm" c="dimmed">
          {start} - {end} of {total}
        </Text>
      )}

      <Box style={{ overflowX: 'auto' }}>
        <DataTable
          striped
          highlightOnHover
          fetching={loading && compositions.length === 0}
          records={compositions}
          columns={columns}
          idAccessor="compositionId"
          noRecordsText="No patients found"
          page={page}
          recordsPerPage={pageSize}
          totalRecords={total}
          onPageChange={onPageChange}
          paginationSize="sm"
          minHeight={220}
          sortStatus={sortStatus}
          styles={{
            table: { minWidth: 1320, tableLayout: 'fixed' },
            header: { fontSize: '0.875rem' },
          }}
          onSortStatusChange={(status: DataTableSortStatus<TriageCompositionData>) => {
            const normalizedAccessor = status.columnAccessor === 'patientName' ? 'name' : status.columnAccessor;
            const next = sortStatusToTriageSortState({
              columnAccessor: normalizedAccessor,
              direction: status.direction,
            });
            if (
              next &&
              sortState &&
              sortState.column === next.column &&
              sortState.direction === 'asc' &&
              next.direction === 'desc'
            ) {
              onSortChange(null);
            } else {
              onSortChange(next);
            }
          }}
        />
      </Box>
    </>
  );
}
